import { Component, DetailEvent, Entity, registerComponent, registerSystem, System } from "aframe";
import { Vector3 } from "three";
import { LocationSystem } from "../location";
import { AmmoBodyComponent } from "../ammo";
import { SlotEvent } from "../slot";


/*

offline/standalone equivalent of rtc-channel

*/


interface SpawnEventDetail
{
	template : string;
	transform? : Entity; //Where to put it.
	variables? : { [key:string] : string|boolean|number };
}
export interface OfflineChannelComponent extends Component
{
	data : {
		template : string;
	};
	spawnListener : EventListener;
	leaveListener : EventListener;
}


interface OfflineChannelSystem extends System
{
	data : { [name : string] : string };
}

registerSystem("offline-channel", {
	schema : {
		"" : { type : "string" } /* [name] : TemplateUrl */
	}
});

registerComponent("offline-channel", {

	schema : {
		template : { type : "string"}
	},

	init(this : OfflineChannelComponent)
	{
		this.el.setAttribute("world-channel", {});

		this.spawnListener = ((event : DetailEvent<SpawnEventDetail> ) =>
		{
			const entity = this.el.ownerDocument.createElement("a-entity") as Entity;
			entity.id = "spanwed#" + Date.now();
			
			const detail = event.detail, slot = event.target;

			if(detail.variables)
			{
				const vars : { [key : string] : String|boolean|number } = {};
				for(let key in detail.variables)
				{
					const val = detail.variables[key];
					vars[key] = (typeof val == 'string') ? new String(val) : val;
				}
				entity.setAttribute("variables", vars);
			}
			if(detail.transform) //Just copy target transform and skip the attach stuff
			{
				detail.transform.object3D.matrixWorld.decompose( entity.object3D.position, entity.object3D.quaternion, {} as any );
			}
			else if(slot.components && slot.components.slot)
			{
				entity.object3D.visible = false;

				//Try to attach each time a template is loaded :
				let templateCount = 0;
				const templateLoading = () => ++templateCount;
				const templateLoaded = () => {
					entity.emit("attach-to-slot", slot);
					--templateCount;
					
					if(templateCount > 0) return; //Still a chance...
					if(entity.object3D.visible) return; //Already good!
					
					//Align to slot with default matrix :
					slot.object3D.updateWorldMatrix(true, false);
					slot.object3D.matrixWorld.decompose(  entity.object3D.position, entity.object3D.quaternion, new Vector3() );
					entity.object3D.updateWorldMatrix(true, true);
					
					const ammoBody = entity.components["ammo-body"] as AmmoBodyComponent;
					ammoBody && ammoBody.moveTo( entity.object3D.position, entity.object3D.quaternion );
					
					//Simulate aligned event to remove listeners and show entity
					const detail = {
						slot,
						item : entity,
						anchor : entity
					};
					entity.emit("aligned-to-slot", detail, false );
					slot.emit("item-aligned", detail, false );
				}

				const attached = ((event : SlotEvent) =>
				{
					const detail = event.detail;
					if(detail.slot !== slot) return;
					entity.removeEventListener("attached-to-slot", attached);
					entity.removeEventListener("aligned-to-slot", attached);
					entity.removeEventListener("template-loaded", templateLoaded );
					entity.removeEventListener("template-loading", templateLoading );
					entity.object3D.visible = true;

					if(entity.components["ammo-body"])
						entity.setAttribute("ammo-body", { enabled : true } );
				}) as EventListener;

				entity.addEventListener("attached-to-slot", attached );
				entity.addEventListener("aligned-to-slot", attached );
				entity.addEventListener("template-loaded", templateLoaded );
				entity.addEventListener("template-loading", templateLoading );
			}

			entity.setAttribute("offline-entity", { template : detail.template } );
			this.el.append(entity);
		}) as EventListener;

		this.leaveListener = () => 
		{
			(this.el.sceneEl!.systems.location as  LocationSystem).removeChannel(this.data.template);
			this.el.classList.remove("joined");
			this.el.emit("channel-left", this);
			this.el.remove();
		};

		this.el.addEventListener("spawn-entity", this.spawnListener);
		this.el.addEventListener("leave-channel", this.leaveListener, {once: true});
	},

	update(this : OfflineChannelComponent)
	{
		const system = this.system as OfflineChannelSystem;
		const templateUrl = system.data[this.data.template] || this.data.template;
		this.el.setAttribute("template__offline-channel", templateUrl );
	},
	
	remove(this : OfflineChannelComponent)
	{
		this.el.removeAttribute("world-channel");
		this.el.removeEventListener("spawn-entity", this.spawnListener);
		this.el.removeEventListener("leave-channel", this.leaveListener);	
	}

});